import { ToolsConfiguration, VipButton, VipInfo } from '@3ddv/ngx-sites-builder-lib';
import { Component } from '@angular/core';
import { VipinfoEditColorDisplay } from './models/vipinfo-edit-colors.model';
import { Subject, take, takeUntil } from 'rxjs';
import { ToolsService } from '../services/tools.service';
import { SblibService } from '../../services/sblib.service';
import { ToastrService } from 'ngx-toastr';
import { VWVipButton } from './models/vipinfo.model';
import { v4 as uuidv4 } from 'uuid';
import {
  VipDefaultButton,
  VipLinkButton,
} from '@3ddv/ngx-sites-builder-lib/lib/workflows/interface/vipinfo/models/vipinfo.model';

@Component({
  selector: 'app-vipinfo',
  templateUrl: './vipinfo.component.html',
  styleUrls: ['./vipinfo.component.scss'],
})
export class VipinfoComponent {
  public vipButtons: VWVipButton[] = [];
  // vipinfo tool
  public tool: VipInfo;
  // tab with -> contents - colors
  public tabs = {
    active: 'contents',
  };
  // variable to store if user is edditing any button
  public isEditing: boolean;
  // variable to store the current selected button to edit
  public editingButton: VWVipButton;
  // variable to store [label,property,inputType] of the edit color fields
  public vipInfoEditColorsDisplay: { [key: string]: VipinfoEditColorDisplay[] };
  public defaultButton: VipDefaultButton;
  public linkButton: VipLinkButton;
  // unsubscriber to avoid memory leaks
  private _unsubscribe$: Subject<void>;

  constructor(
    private _toolsService: ToolsService, // service to get the available tools
    private _sblibService: SblibService, // service to update the changes made in thetool
    private _toastrService: ToastrService // service for displaying alerts
  ) {
    // initialize variables
    this.tool = null;
    this._unsubscribe$ = new Subject<void>();
    this.isEditing = false;
    this.editingButton = null;
    this.vipInfoEditColorsDisplay = {
      Buttons: [
        { property: 'buttonColor', label: 'Button Background' },
        { property: 'buttonTextColor', label: 'Button Text' },
        { property: 'buttonBorderColor', label: 'Button Border' },
        { property: 'hoverButtonBackgroundColor', label: 'Button Hover Background' },
        { property: 'hoverButtonTextColor', label: 'Button Hover Text' },
        { property: 'popupHeaderColor', label: 'Popup Header' },
        { property: 'popupTitleColor', label: 'Popup Title' },
        { property: 'popupContentColor', label: 'Popup Content' },
        { property: 'closeButtonColor', label: 'Close Background' },
        { property: 'closeButtonHoverBackgroundColor', label: 'Close Hover Background' },
        { property: 'closeButtonHoverTextColor', label: 'Close Hover Text' },
        { property: 'closeButtonTextColor', label: 'Close Text' },
      ],
      Links: [
        { property: 'buttonColor', label: 'Button Background' },
        { property: 'buttonTextColor', label: 'Button Text' },
        { property: 'buttonBorderColor', label: 'Button Border' },
        { property: 'hoverButtonBackgroundColor', label: 'Button Hover Background' },
        { property: 'hoverButtonTextColor', label: 'Button Hover Text' },
      ],
    };
  }

  ngOnInit(): void {
    this._initializeTool();
  }

  // function to get the tool information
  _initializeTool(): void {
    this._toolsService
      .getAsyncTools$()
      .pipe(take(1), takeUntil(this._unsubscribe$))
      .subscribe({
        next: (tools: ToolsConfiguration) => this._handleInitializeSuccess(tools),
        error: (error: any) => this._handleError(error),
        complete: () => {},
      });
  }

  // function to handle success response when getting the tool information
  _handleInitializeSuccess(tools: ToolsConfiguration): void {
    if (!tools?.vipinfo) return;
    // create a deep copy of the object in order to make changes without changing the current object itself
    this.tool = JSON.parse(JSON.stringify(tools.vipinfo));
    // initialize button id's
    this._initializeButtonsIdsAfterInit();
  }
  _handleError(error?: any): void {
    // notify the error to the user through a toast
    this._toastrService.error('Error', 'An error occured, please try again later.');
  }

  apply(): void {
    this._toolsService
      .getAsyncTools$()
      .pipe(take(1), takeUntil(this._unsubscribe$))
      .subscribe({
        next: (tools: ToolsConfiguration) => this._saveTool(tools),
        error: (error: any) => console.log(error),
        complete: () => {},
      });
  }

  _saveTool(tools: ToolsConfiguration): void {
    // set tools vipinfo object as a deep copy of the current 'this.tool'
    // this is set as so in order to not alter the current object until the
    // 'apply changes' button is pressed. To sum up, a copy is made to not make
    // any changes visible to the user until the button 'apply changes' is clicked.

    try {
      // set the tools vipinfo as the current ( updating its value)
      this.tool.vipButtons = JSON.parse(JSON.stringify(this.vipButtons));
      tools.vipinfo = JSON.parse(JSON.stringify(this.tool));
      // before saving the tool, remove all references to ID'S
      this._removeIdsBeforeSave(tools);
      // proceed to save changes
      this._sblibService.apply(tools);
      this._toastrService.success('Site updated successfully.', 'Success');
    } catch (error) {
      this._toastrService.success('An error has occured, try again later.', 'Error');
    }
  }

  // function to remove the ids from the tool buttons before saving
  _removeIdsBeforeSave(tools: ToolsConfiguration): void {
    tools.vipinfo.vipButtons.map((button: VWVipButton) => {
      delete button.id;
      const parsedButton: VipButton = button;
      return parsedButton;
    });
  }

  // function to initialize buttons id's
  _initializeButtonsIdsAfterInit(): void {
    this.tool.vipButtons.map((button: VWVipButton) => {
      const vwButton: VWVipButton = {
        ...button,
        id: uuidv4(),
      };
      this.vipButtons.push(vwButton);
      if (!this.defaultButton && button.type === 'button') {
        this.defaultButton = button;
      } else if (!this.linkButton && button.type === 'link') {
        this.linkButton = button;
      }
    });
  }

  // function to edit the button properties
  onEdit(button: VWVipButton): void {
    // set editing mode to true
    this.isEditing = true;
    // set the current editing button as the one passed as an argument
    this.editingButton = button;
  }

  // function to remove the button
  _removeButton(id: string): void {
    // find index of the button
    const buttonIndex = this._findIndexOfButtonById(id);
    if (buttonIndex !== -1) {
      this.vipButtons.splice(buttonIndex, 1);
      this.linkButton = this.vipButtons.some(button => button.type === 'link') ? this.linkButton : null;
      this.defaultButton = this.vipButtons.some(button => button.type === 'button') ? this.defaultButton : null;
    }
  }

  _findIndexOfButtonById(id: string): number {
    const index = this.vipButtons.findIndex((button: VWVipButton) => button.id === id);
    return index;
  }

  // function to update button properties
  _updateButton(updatedButton: VWVipButton): void {
    const buttonIndex = this._findIndexOfButtonById(updatedButton.id);
    if (buttonIndex != -1) {
      // update button
      this.vipButtons[buttonIndex] = updatedButton;
    }
  }

  ////////////////////////////////////////
  //  FUNCTIONS TO HANDLE CHILD EVENTS  //
  ///////////////////////////////////////

  onCreateButton(button: VWVipButton): void {
    this.vipButtons = [...this.vipButtons, button];
    if (!this.defaultButton && button.type === 'button') {
      this.defaultButton = button;
    } else if (!this.linkButton && button.type === 'link') {
      this.linkButton = button;
    }
  }

  //function to hide the modal
  onCancel(): void {
    // change editing mode
    this.isEditing = false;
    //set current editing item to null since user is no longer editing
    this.editingButton = null;
  }

  onSave(updatedButton: VWVipButton): void {
    // change editing mode
    this.isEditing = false;
    //set current editing item to null since user is no longer editing
    this.editingButton = null;
    // TODO: save button updated state
    this._updateButton(updatedButton);
  }

  onDelete(id: string): void {
    // change editing mode
    this.isEditing = false;
    //set current editing item to null since user is no longer editing
    this.editingButton = null;
    //remove button from buttons list
    this._removeButton(id);
  }

  // function to handle the color change of the colors tab
  onColorChange(colorInfo: { category: string; property: string; color: string }): void {
    if (!this.tool || !this.tool.vipButtons) return;
    if (colorInfo.category === 'Links') {
      const linkButtons: VWVipButton[] = this.vipButtons.filter(button => button.type === 'link');
      linkButtons.forEach((button: VWVipButton) => {
        button.styles[colorInfo.property] = colorInfo.color;
      });
    } else if (colorInfo.category === 'Buttons') {
      const defaultButtons: VWVipButton[] = this.vipButtons.filter(button => button.type === 'button');
      defaultButtons.forEach((button: VWVipButton) => {
        button.styles[colorInfo.property] = colorInfo.color;
      });
    }
  }

  ////////////////////////////////////////////////////////

  // unsubscribe from the observables in order to avoid memory leaks
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
